import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "@modules/auth/contexts/auth-plus.context";
import {
  AuthArgs,
  MarketsView,
  SearchConfigService,
} from "../services/search-config.service";

export default function MarketsPanel() {
  const { auth, setAuth, logout } = useContext(AuthContext);
  const args: AuthArgs = { auth, setAuth, logout };

  const [view, setView] = useState<MarketsView | null>(null);
  const [live, setLive] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [onlyWithSupply, setOnlyWithSupply] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const apply = (v: MarketsView) => {
    setView(v);
    setLive(new Set(v.markets.filter((m) => m.live).map((m) => m.code)));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      apply(await SearchConfigService.getMarkets({ auth, setAuth, logout }));
    } catch (e: any) {
      setError(e?.message || "Failed to load markets");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  useEffect(() => {
    load();
  }, [load]);

  const saved = useMemo(
    () => new Set(view?.markets.filter((m) => m.live).map((m) => m.code)),
    [view],
  );
  const dirty =
    !!view &&
    (saved.size !== live.size || Array.from(live).some((c) => !saved.has(c)));

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (view?.markets ?? []).filter(
      (m) =>
        (!q || m.name.toLowerCase().includes(q) || m.code.toLowerCase().includes(q)) &&
        (!onlyWithSupply || m.trainers + m.gyms + m.packages > 0),
    );
  }, [view, query, onlyWithSupply]);

  const toggle = (code: string) => {
    const next = new Set(live);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setLive(next);
  };

  const save = async () => {
    if (live.size === 0) return setError("Keep at least one market live.");
    // Switching off a market that has supply hides it from users there, so ask first.
    const hiding = (view?.markets ?? []).filter(
      (m) => saved.has(m.code) && !live.has(m.code) && m.trainers + m.gyms + m.packages > 0,
    );
    if (
      view?.configured &&
      hiding.length > 0 &&
      !window.confirm(
        `Hide ${hiding.map((m) => m.name).join(", ")}? They have trainers, gyms or packages, and people there will no longer see them in the region list.`,
      )
    )
      return;
    setSaving(true);
    setError(null);
    try {
      apply(await SearchConfigService.saveMarkets(args, Array.from(live)));
      setNotice("Saved. The apps pick this up within about 30 seconds.");
      setTimeout(() => setNotice(null), 5000);
    } catch (e: any) {
      setError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Offer every market again?")) return;
    setSaving(true);
    setError(null);
    try {
      apply(await SearchConfigService.resetMarkets(args));
      setNotice("Every market is offered again.");
      setTimeout(() => setNotice(null), 5000);
    } catch (e: any) {
      setError(e?.message || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  if (!view) {
    return (
      <div>
        {error && <div className="alert alert-danger">{error}</div>}
        {loading && <div className="text-muted">Loading…</div>}
      </div>
    );
  }

  return (
    <div>
      <p className="text-muted small">
        Markets the apps offer in the region picker. Search results, packages and
        gyms are matched by market, so open a country only when it has enough
        trainers, gyms and packages. Numbers are what search can return today
        (refreshed every minute). Until you save a selection every market is
        offered. The default market ({view.default_market}) and the market
        filter itself are still set in the server configuration.
      </p>

      {error && <div className="alert alert-danger">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <input
          className="form-control"
          style={{ maxWidth: 260 }}
          placeholder="Search country or code"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="form-check mb-0">
          <input
            id="mk-supply"
            type="checkbox"
            className="form-check-input"
            checked={onlyWithSupply}
            onChange={(e) => setOnlyWithSupply(e.target.checked)}
          />
          <label htmlFor="mk-supply" className="form-check-label">
            Only countries with supply
          </label>
        </div>
        <span className="small text-muted ms-auto">
          {live.size} live of {view.markets.length}
          {!view.configured && " (nothing saved yet: all are offered)"}
        </span>
      </div>

      <div className="card shadow-sm border-0 mb-3">
        <div className="table-responsive" style={{ maxHeight: 520 }}>
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light" style={{ position: "sticky", top: 0 }}>
              <tr>
                <th className="ps-4" style={{ width: 90 }}>Live</th>
                <th>Country</th>
                <th className="text-end">Trainers</th>
                <th className="text-end">Gyms</th>
                <th className="text-end pe-4">Packages</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-4 text-muted">
                    No country matches.
                  </td>
                </tr>
              )}
              {rows.map((m) => (
                <tr key={m.code}>
                  <td className="ps-4">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={live.has(m.code)}
                        onChange={() => toggle(m.code)}
                        aria-label={`${m.name} live`}
                      />
                    </div>
                  </td>
                  <td>
                    {m.name} <span className="text-muted small">{m.code}</span>
                    {m.code === view.default_market && (
                      <span className="badge bg-secondary ms-2">default</span>
                    )}
                  </td>
                  <td className="text-end">{m.trainers}</td>
                  <td className="text-end">{m.gyms}</td>
                  <td className="text-end pe-4">{m.packages}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="d-flex gap-3">
        <button
          className="btn btn-primary"
          disabled={!dirty || saving}
          onClick={save}
        >
          {saving ? "Saving…" : "Save markets"}
        </button>
        <button
          className="btn btn-outline-secondary"
          disabled={saving || !view.configured}
          onClick={reset}
        >
          Offer every market
        </button>
      </div>
    </div>
  );
}
